import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { usePaymentDesignTokens, PAYMENT_TYPOGRAPHY, PAYMENT_SPACING } from '@/constants/residents/mobile/paymentDesignTokens';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

const ChevronRight = RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT;

interface MobilePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

/**
 * Mobile Pagination Component
 * Matches Figma design for transaction history pagination
 * Shows: First | < | "X of Y" | > | Last
 */
export const MobilePagination: React.FC<MobilePaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
}) => {
  const pt = usePaymentDesignTokens();

  const styles = useMemo(() => StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: PAYMENT_SPACING.lg,
      gap: PAYMENT_SPACING.sm,
    },
    button: {
      borderWidth: 1,
      borderColor: pt.BORDER,
      borderRadius: 6,
      backgroundColor: 'transparent',
    },
    textButton: {
      paddingHorizontal: PAYMENT_SPACING.md,
      paddingVertical: PAYMENT_SPACING.sm,
    },
    iconButton: {
      width: 36,
      height: 36,
      alignItems: 'center',
      justifyContent: 'center',
    },
    buttonDisabled: {
      borderColor: pt.TEXT_TERTIARY,
      opacity: 0.5,
    },
    buttonText: {
      ...PAYMENT_TYPOGRAPHY.textXsMedium,
      color: pt.TEXT_PRIMARY,
    },
    buttonTextDisabled: {
      color: pt.TEXT_TERTIARY,
    },
    chevronText: {
      ...PAYMENT_TYPOGRAPHY.bodySmMedium,
      color: pt.TEXT_PRIMARY,
    },
    pageInfo: {
      paddingHorizontal: PAYMENT_SPACING.md,
    },
    pageInfoText: {
      ...PAYMENT_TYPOGRAPHY.textXsMedium,
      color: pt.TEXT_PRIMARY,
    },
    rotatedIcon: {
      transform: [{ rotate: '180deg' }],
    },
}), [pt]);

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  const handleFirst = () => {
    if (!isFirstPage && !disabled) {
      onPageChange(1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstPage && !disabled) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (!isLastPage && !disabled) {
      onPageChange(currentPage + 1);
    }
  };

  const handleLast = () => {
    if (!isLastPage && !disabled) {
      onPageChange(totalPages);
    }
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <View style={styles.container}>
      {/* First Button */}
      <TouchableOpacity
        style={[styles.button, styles.textButton, (isFirstPage || disabled) && styles.buttonDisabled]}
        onPress={handleFirst}
        disabled={isFirstPage || disabled}
        accessibilityLabel="Go to first page"
        accessibilityRole="button"
      >
        <Text style={[styles.buttonText, (isFirstPage || disabled) && styles.buttonTextDisabled]}>
          First
        </Text>
      </TouchableOpacity>

      {/* Previous Button */}
      <TouchableOpacity
        style={[styles.button, styles.iconButton, (isFirstPage || disabled) && styles.buttonDisabled]}
        onPress={handlePrevious}
        disabled={isFirstPage || disabled}
        accessibilityLabel="Go to previous page"
        accessibilityRole="button"
      >
        {ChevronRight ? (
          <View style={styles.rotatedIcon}>
            <ChevronRight
              width={16}
              height={16}
              color={(isFirstPage || disabled) ? pt.TEXT_TERTIARY : pt.TEXT_PRIMARY}
            />
          </View>
        ) : (
          <Text style={[styles.chevronText, (isFirstPage || disabled) && styles.buttonTextDisabled]}>{'<'}</Text>
        )}
      </TouchableOpacity>

      {/* Page Info */}
      <View style={styles.pageInfo}>
        <Text style={styles.pageInfoText}>
          {currentPage} of {totalPages}
        </Text>
      </View>

      {/* Next Button */}
      <TouchableOpacity
        style={[styles.button, styles.iconButton, (isLastPage || disabled) && styles.buttonDisabled]}
        onPress={handleNext}
        disabled={isLastPage || disabled}
        accessibilityLabel="Go to next page"
        accessibilityRole="button"
      >
        {ChevronRight ? (
          <ChevronRight
            width={16}
            height={16}
            color={(isLastPage || disabled) ? pt.TEXT_TERTIARY : pt.TEXT_PRIMARY}
          />
        ) : (
          <Text style={[styles.chevronText, (isLastPage || disabled) && styles.buttonTextDisabled]}>{'>'}</Text>
        )}
      </TouchableOpacity>

      {/* Last Button */}
      <TouchableOpacity
        style={[styles.button, styles.textButton, (isLastPage || disabled) && styles.buttonDisabled]}
        onPress={handleLast}
        disabled={isLastPage || disabled}
        accessibilityLabel="Go to last page"
        accessibilityRole="button"
      >
        <Text style={[styles.buttonText, (isLastPage || disabled) && styles.buttonTextDisabled]}>
          Last
        </Text>
      </TouchableOpacity>
    </View>
  );
};

